import { FC, useEffect, useRef, useState } from "react";
import { Box, Stack, SystemProps, Text } from "@storyofams/react-ui";
import { ProductOption } from "~lib/shopify/sdk";
import { SingleVariant } from "../SingleVariant";

interface SingleOptionProps extends SystemProps {
  autoDiscount: string | null;
  refillVariants: any[];
  regularVariants: any[];
  options: Pick<ProductOption, "name" | "values">[];
  mostPopularVariant: string;
}

export const SingleOption: FC<SingleOptionProps> = ({
  regularVariants,
  refillVariants,
  autoDiscount,
  mostPopularVariant,
  options,
  ...props
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setShowTopFade(scrollTop > 2);
    setShowBottomFade(scrollTop + clientHeight < scrollHeight - 2);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    handleScroll();
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box position="relative" {...props}>
      {/* Scrollable content box */}
      <Box
        ref={scrollRef}
        overflowY="auto"
        pr={0}
        px={0}
        style={{ maxHeight: "300px", paddingTop: "4px", paddingBottom: "4px" }}
      >
        <Text color={"#767473"} fontSize={"14px"} mb={1}>
          {options[0].name}
        </Text>

        <Stack flexDirection={"column"} space={1}>
          {regularVariants.map(([key, value]) => (
            <SingleVariant
              key={key}
              option={value[0]}
              autoDiscount={autoDiscount}
              mostPopularVariant={mostPopularVariant}
            />
          ))}
        </Stack>

        {!!refillVariants.length && (
          <>
            <Text mt={2} color={"#767473"} fontSize={"14px"} mb={1}>
              {"Refill / Bulk"}
            </Text>
            <Stack flexDirection={"column"} space={1}>
              {refillVariants.map(([key, value]) => (
                <SingleVariant
                  key={key}
                  option={value[0]}
                  autoDiscount={autoDiscount}
                  mostPopularVariant={mostPopularVariant}
                />
              ))}
            </Stack>
          </>
        )}
      </Box>

      {/* Top Fade */}
      {showTopFade && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          height="44px"
          pointerEvents="none"
          css={{
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0.8), rgba(255,255,255,0))",
          }}
        />
      )}

      {/* Bottom Fade */}
      {showBottomFade && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          height="44px"
          pointerEvents="none"
          css={{
            background:
              "linear-gradient(to top, rgba(255,255,255,0.8), rgba(210, 209, 209, 0))",
          }}
        />
      )}
    </Box>
  );
};
